import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Check } from "@mui/icons-material";
import { useDashboardFooter } from "../../contexts/DashboardFooterContext";
import { SkillRanker } from "../homeowner-preferences-quiz/SkillRanker";
import { gql, useMutation } from "@apollo/client";

const UPDATE_PROFILE = gql`
  mutation UpdateHomeOwnerMatchingProfile(
    $_id: ID!
    $stepCompletion: HomeOwnerMatchingProfileStepCompletionInput
    $priorities: HomeOwnerPrioritiesInput
    $preferences: HomeOwnerMatchingProfilePreferenceInput
  ) {
    updateHomeOwnerMatchingProfile(
      _id: $_id
      stepCompletion: $stepCompletion
      priorities: $priorities
      preferences: $preferences
    )
  }
`;

// Keep a key (GraphQL enum) + label for UI.
const PRIORITY_OPTIONS = [
  { key: "COMPATIBLE_PERSONALITIES", label: "Compatible Personalities" },
  { key: "BUDGET", label: "Budget" },
  { key: "TIMELINE", label: "Timeline" },
  { key: "HOW_DESIGNER_BILLS", label: "How the Designer Bills" },
  { key: "STYLE", label: "Style" },
  { key: "PROJECT_FINANCING", label: "Project Financing is Offered" },
  { key: "DESIGNER_YEARS_EXPERIENCE", label: "Designer Years of Experience" },
  { key: "COMPATIBLE_PERSONAL_PREFERENCES", label: "Compatible Personal Preferences" },
  { key: "IDEAL_DESIGNER_TRAITS", label: "Ideal Designer Traits" },
  { key: "PROJECT_SPECIAL_NEEDS", label: "Project Special Needs" },
] as const;

const HIRE_LIKELIHOOD_OPTIONS = [
  "It's an absolute, just need to find the right designer",
  "Highly likely, with the right match",
  "Not likely, just dreaming/researching options",
] as const;

const normalizeHireLikelihoodValue = (value?: string | null) => {
  const normalized = value?.trim().toLowerCase() ?? "";
  if (!normalized) return "";
  if (normalized.includes("absolute")) {
    return HIRE_LIKELIHOOD_OPTIONS[0];
  }
  if (normalized.includes("highly likely") || normalized.includes("warm opportunity")) {
    return HIRE_LIKELIHOOD_OPTIONS[1];
  }
  if (normalized.includes("not likely")) {
    return HIRE_LIKELIHOOD_OPTIONS[2];
  }
  return "";
};

const removeTypenameDeep = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(removeTypenameDeep);
  }
  if (value && typeof value === "object") {
    const result: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, entry]) => {
      if (key === "__typename") return;
      result[key] = removeTypenameDeep(entry);
    });
    return result;
  }
  return value;
};

type Props = {
  profileId: string;
  initialOrder?: string[];
  initialHireLikelihood?: string | null;
  existingPreferences?: Record<string, unknown> | null;
  onBack?: () => void;
  onDone?: () => void;
};

export default function HomeownerPriorities({
  profileId,
  initialOrder,
  initialHireLikelihood,
  existingPreferences,
  onBack,
  onDone,
}: Props) {
  const { setFooter, clearFooter } = useDashboardFooter();
  const [subStep, setSubStep] = useState<1 | 2>(1);

  // UI shows labels; we internally keep keys in the same order.
  const defaultKeys = PRIORITY_OPTIONS.map((p) => p.key);
  const [orderKeys, setOrderKeys] = useState<string[]>(() => {
    const value =
      Array.isArray(initialOrder) && initialOrder.length === defaultKeys.length
        ? initialOrder
        : defaultKeys;
    return value;
  });
  const [hireLikelihood, setHireLikelihood] = useState<string>(() =>
    normalizeHireLikelihoodValue(initialHireLikelihood)
  );

  useEffect(() => {
    if (hireLikelihood) return;
    const normalized = normalizeHireLikelihoodValue(initialHireLikelihood);
    if (normalized) {
      setHireLikelihood(normalized);
    }
  }, [initialHireLikelihood, hireLikelihood]);

  // Update orderKeys when initialOrder changes (for retake flow)
  // IMPORTANT: Only update if initialOrder actually changed and is different from current orderKeys
  // This prevents overwriting user's dragged order when component re-renders
  useEffect(() => {
    // Only update if initialOrder is different from current orderKeys
    // This prevents resetting user's dragged order on re-renders
    if (Array.isArray(initialOrder) && initialOrder.length === defaultKeys.length) {
      const currentOrderString = JSON.stringify(orderKeys);
      const newOrderString = JSON.stringify(initialOrder);

      if (currentOrderString !== newOrderString) {
        setOrderKeys(initialOrder);
      }
    } else if (Array.isArray(initialOrder) && initialOrder.length > 0) {
      // If initialOrder has items but not the full set, still use it (might be partial data)
      const currentOrderString = JSON.stringify(orderKeys);
      const newOrderString = JSON.stringify(initialOrder);

      if (currentOrderString !== newOrderString) {
        setOrderKeys(initialOrder);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOrder, defaultKeys.length]); // Intentionally not including orderKeys to prevent overwriting user changes

  const labelsForUI = useMemo(
    () =>
      orderKeys
        .map((k) => PRIORITY_OPTIONS.find((p) => p.key === k)?.label)
        .filter(Boolean) as string[],
    [orderKeys]
  );

  const [mutate, { loading }] = useMutation(UPDATE_PROFILE);

  const save = useCallback(async () => {
    if (!hireLikelihood) return;
    const toSend =
      orderKeys.length === defaultKeys.length ? orderKeys : defaultKeys;

    const cleanStepCompletion = {
      prioritiesCompleted: true,
    };

    const cleanedPreferences =
      (removeTypenameDeep(existingPreferences ?? {}) as Record<string, unknown>) ?? {};
    const preferencesPayload = {
      ...cleanedPreferences,
      hireLikelihood,
    };

    await mutate({
      variables: {
        _id: profileId,
        priorities: { order: toSend },
        preferences: preferencesPayload,
        stepCompletion: cleanStepCompletion,
      },
    });
    onDone?.();
  }, [
    hireLikelihood,
    orderKeys,
    defaultKeys,
    existingPreferences,
    profileId,
    mutate,
    onDone,
  ]);

  const handleBack = useCallback(() => {
    if (subStep === 1) {
      onBack?.();
      return;
    }
    setSubStep(1);
  }, [onBack, subStep]);

  const handleNext = useCallback(async () => {
    if (subStep === 1) {
      if (!hireLikelihood) return;
      setSubStep(2);
      return;
    }
    await save();
  }, [subStep, hireLikelihood, save]);

  // footer
  useEffect(() => {
    setFooter(
      <>
        <Button
          onClick={handleBack}
          variant="text"
          startIcon={<ChevronLeft sx={{ width: 16, height: 16 }} />}
          sx={{ fontSize: { xs: "14px", sm: "16px" } }}
        >
          Back
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          endIcon={subStep === 1 ? <ChevronRight sx={{ width: 16, height: 16 }} /> : <Check />}
          disabled={loading || (subStep === 1 && !hireLikelihood)}
          sx={{ fontSize: { xs: "14px", sm: "16px" } }}
        >
          {subStep === 1 ? "Continue 1 of 2" : "Complete Profile"}
        </Button>
      </>
    );
    return () => clearFooter();
  }, [
    setFooter,
    clearFooter,
    handleBack,
    handleNext,
    loading,
    subStep,
    hireLikelihood,
  ]);

  return (
    <Box pt={0} pb={{ xs: 2, md: 4 }}>
      <Container maxWidth="lg">
        {subStep === 1 ? (
          <Box textAlign="center" mb={5}>
            <Typography
              sx={{ fontSize: "0.95rem", fontWeight: 700, color: "secondary.main", mb: 1 }}
            >
              Question 1
            </Typography>
            <Typography
              variant="h5"
              fontWeight="bold"
              gutterBottom
              sx={{ fontSize: { xs: "20.3px", sm: "1.5rem" } }}
            >
              How likely are you to hire a designer in the next 30 days?
            </Typography>
            <Grid container spacing={2} justifyContent="center" mt={0.5}>
              {HIRE_LIKELIHOOD_OPTIONS.map((option) => {
                const selected = hireLikelihood === option;
                return (
                  <Grid item xs={12} sm={6} md={4} key={option}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderColor: selected ? "secondary.main" : "secondary.light",
                        bgcolor: selected ? "secondary.main" : "transparent",
                        color: selected ? "#fff" : "secondary.main",
                        height: 160,
                        transition: "all 0.2s ease",
                      }}
                    >
                      <CardActionArea
                        onClick={() => setHireLikelihood(option)}
                        sx={{ height: "100%" }}
                      >
                        <CardContent sx={{ height: "100%", display: "flex", alignItems: "center" }}>
                          <Typography
                            fontWeight={600}
                            color="inherit"
                            sx={{ width: "100%", textAlign: "center", lineHeight: 1.35 }}
                          >
                            {option}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ) : (
          <>
            <Box textAlign="center" mb={3}>
              <Typography
                sx={{ fontSize: "0.95rem", fontWeight: 700, color: "secondary.main", mb: 1 }}
              >
                Question 2
              </Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{ fontSize: { xs: "20.3px", sm: "1.5rem" } }}
              >
                If matched with many designers, please rank the following in order of
                importance to you.
              </Typography>
            </Box>

            <SkillRanker
              items={PRIORITY_OPTIONS.map((p) => p.label)}
              value={labelsForUI}
              onChange={(orderedLabels) => {
                const nextKeys = orderedLabels
                  .map((label) => {
                    const hit = PRIORITY_OPTIONS.find((p) => p.label === label);
                    return hit?.key ?? "";
                  })
                  .filter(Boolean) as string[];
                const finalKeys =
                  nextKeys.length === defaultKeys.length ? nextKeys : orderKeys;
                setOrderKeys(finalKeys);
              }}
            />
          </>
        )}
      </Container>
    </Box>
  );
}
